import { create } from 'zustand'
import {
  Contact,
  ContactPagination,
  ContactSummary,
  CreateContactRequest,
  UpdateContactRequest,
  ContactStatus
} from '../types/contact'
import { contactService } from '../services/contactService'

interface ContactState {
  // State
  contacts: Contact[]
  summary: ContactSummary | null
  pagination: ContactPagination | null
  loading: boolean
  error: string | null

  // Actions
  loadContacts: (
    customerId: string,
    search?: string,
    partnerId?: string,
    status?: ContactStatus,
    roleId?: string,
    isPrimary?: boolean,
    page?: number,
    limit?: number
  ) => Promise<void>
  createContact: (customerId: string, data: CreateContactRequest) => Promise<Contact>
  updateContact: (id: string, customerId: string, data: UpdateContactRequest) => Promise<Contact>
  deleteContact: (id: string, customerId: string) => Promise<void>
  reset: () => void
}

const useContactStore = create<ContactState>((set) => ({
  // Initial State
  contacts: [],
  summary: null,
  pagination: null,
  loading: false,
  error: null,

  // Actions
  loadContacts: async (
    customerId: string,
    search?: string,
    partnerId?: string,
    status?: ContactStatus,
    roleId?: string,
    isPrimary?: boolean,
    page?: number,
    limit?: number
  ) => {
    set({ loading: true, error: null })
    try {
      const { data, summary, pagination } = await contactService.getContacts(
        customerId,
        search,
        partnerId,
        status,
        roleId,
        isPrimary,
        page,
        limit
      )
      set({ contacts: data, summary, pagination, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load contacts',
        loading: false
      })
    }
  },

  createContact: async (customerId: string, data: CreateContactRequest) => {
    const contact = await contactService.createContact(customerId, data)
    // Note: We don't add to local state here because the API response may not include
    // the full partners and contact_roles objects needed for display.
    // The ContactsList component will reload contacts after successful creation.
    return contact
  },

  updateContact: async (id: string, customerId: string, data: UpdateContactRequest) => {
    const updated = await contactService.updateContact(id, customerId, data)
    // Note: We don't update local state here because the API response may not include
    // the full partners and contact_roles objects needed for display.
    // The EditContactDrawer component will reload contacts after successful update.
    return updated
  },

  deleteContact: async (id: string, customerId: string) => {
    await contactService.deleteContact(id, customerId)

    // Remove from local state
    set((state) => ({
      contacts: state.contacts.filter((contact) => contact.id !== id)
    }))
  },

  reset: () =>
    set({
      contacts: [],
      summary: null,
      pagination: null,
      loading: false,
      error: null
    })
}))

export default useContactStore
